"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl py-12 px-4 sm:px-6 lg:px-8 animate-pulse">
        <div className="flex justify-center gap-2 mb-6">
          <div className="h-4 w-20 bg-muted rounded-md"></div>
          <div className="h-4 w-24 bg-muted rounded-md"></div>
        </div>
        <div className="h-12 w-3/4 bg-muted rounded-md mx-auto mb-4"></div>
        <div className="h-12 w-1/2 bg-muted rounded-md mx-auto mb-8"></div>
        <div className="flex justify-center items-center gap-4 mb-12">
          <div className="size-8 rounded-lg bg-muted"></div>
          <div className="h-4 w-32 bg-muted rounded-md"></div>
        </div>
        <div className="w-full h-[400px] bg-muted mb-12"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-5/6 bg-muted rounded-md"></div>
          <div className="h-4 w-full bg-muted rounded-md"></div>
          <div className="h-4 w-4/5 bg-muted rounded-md"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl py-24 px-4 text-center">
        <h2 className="font-default text-2xl font-bold mb-4 text-foreground">Unable to load technical insight</h2>
        <p className="text-muted-foreground mb-6">An error occurred while retrieving our latest IT analysis.</p>
        <Button onClick={refetch} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary/10">Retry Connection</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="mx-auto max-w-3xl py-24 px-4 text-center">
        <h2 className="font-default text-3xl font-bold mb-4 text-foreground">Insight Not Found</h2>
        <p className="text-muted-foreground mb-8">The technical documentation or article you are looking for may have been archived or moved.</p>
        <Link to="/HomePage">
          <Button className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90">Return to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-3xl py-12 px-4 sm:px-6 lg:px-8">
      {}
      <header className="mb-10 text-center">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap justify-center gap-3">
            {post.categories.map((cat, i) => (
              <Link 
                key={cat.id} 
                to={cat.link || "#"} 
                className="text-xs font-bold text-secondary uppercase tracking-widest hover:text-secondary/80 transition-colors"
                data-index={i}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 text-foreground tracking-tight">
          {post.title}
        </h1>
        
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-muted-foreground text-sm font-medium">
          {post.author && (
            <div className="flex items-center gap-3">
              <Avatar className="size-10 border border-border rounded-md">
                <AvatarImage src={post.author.avatar?.['48']} alt={post.author.name} />
                <AvatarFallback className="bg-muted text-foreground font-default rounded-md">
                  {post.author.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {post.author.link ? (
                <Link to={post.author.link} className="hover:text-primary transition-colors">
                  {post.author.name}
                </Link>
              ) : (
                <span>{post.author.name}</span>
              )}
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline text-border">•</span>
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-14">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover rounded-md border border-border shadow-lg" 
          />
        </figure>
      )}

      {}
      <div
        className="prose prose-lg max-w-none font-default text-foreground/90 leading-relaxed [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h2]:tracking-tight [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-bold [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>h4]:font-default [&>h4]:text-xl [&>h4]:font-bold [&>h4]:mt-6 [&>h4]:mb-4 [&>h4]:text-foreground [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:my-8 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:bg-accent/50 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80 [&>img]:w-full [&>img]:h-auto [&>img]:my-8 [&>img]:border [&>img]:border-border [&>img]:rounded-md [&>figure]:my-8 [&>figure>img]:w-full [&>figure>img]:border [&>figure>img]:border-border [&>figure>img]:rounded-md [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-muted-foreground [&>figure>figcaption]:mt-2"
        dangerouslySetInnerHTML={{ __html: post.content || '' }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-6 border-t border-border flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-foreground mr-2">Technical Tags:</span>
          {post.tags.map((tag, i) => (
            <Link 
              key={tag.id} 
              to={tag.link || "#"} 
              className="px-3 py-1 bg-muted hover:bg-primary/10 text-muted-foreground hover:text-primary text-xs font-medium transition-colors rounded-md"
              data-index={i}
            >
              {tag.name}
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <Card className="bg-card border-border rounded-md shadow-md overflow-hidden">
          <CardContent className="p-8 flex flex-col sm:flex-row gap-8 items-center sm:items-start">
            <Avatar className="size-24 border border-border shrink-0 rounded-md">
              <AvatarImage src={post.author.avatar?.['96']} alt={post.author.name} />
              <AvatarFallback className="text-3xl font-default bg-muted text-foreground rounded-md">
                {post.author.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left flex-1">
              <h3 className="font-default text-2xl font-bold mb-2 text-foreground">
                {post.author.name}
              </h3>
              <p className="text-muted-foreground text-base mb-6 leading-relaxed">
                IT Solutions Architect at IT Masters. Specializing in resilient infrastructure, virtualization for hospitality, and strategic technology procurement to drive business efficiency.
              </p>
              {post.author.link && (
                <Link to={post.author.link}>
                  <Button variant="outline" className="rounded-md font-semibold border-primary text-primary hover:bg-primary/10">
                    View Technical Portfolio
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function SinglePostTemplateSection() {
  
  const getQueryParams = () => {
    if (typeof window !== 'undefined' && (window as any).wvcClient) {
      return (window as any).wvcClient.getCurrentWPQueryParams();
    }
    
    return { post_type: 'post', per_page: 1 }; 
  };

  const currentWPQueryParams = getQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="229565" id="single-post-template-section" className="bg-background text-foreground min-h-screen w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}